/**
 * RecentSalesRail — homepage social proof.
 *
 * Surfaces the most recently *sold* seeded listings for the current market
 * so first-time visitors see the platform actually closes deals. Reads
 * from `GET /api/properties/recent-sales?market=...`, which is populated
 * every time an admin flips a seeded listing to `status="sold"` in the
 * Seed Listings admin panel.
 *
 * Design intent:
 *   - No hero image dependence: we render a tinted card with the price
 *     tag as the visual anchor, so listings without photos still look good.
 *   - Market-aware currency + labels via `useMarket()`.
 *   - Silently hides when there are no recent sales — never leaves a
 *     dead section on the homepage.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, MapPin, ArrowRight } from 'lucide-react';
import { useMarket } from '../hooks/useMarket';

const API = process.env.REACT_APP_BACKEND_URL;

// Coarse timeago — good enough for "sold last Tue"-style copy without
// pulling in date-fns just for one widget.
const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.max(0, Math.floor((Date.now() - then) / 86400000));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const RecentSalesRail = () => {
  const market = useMarket();
  const currency = market?.currency_symbol || '€';
  const marketCode = market?.code || 'IE';
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/api/properties/recent-sales`, {
        params: { market: marketCode, limit: 6 },
      })
      .then((r) => { if (!cancelled) setSales(r.data || []); })
      .catch(() => { if (!cancelled) setSales([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [marketCode]);

  // Silent-hide rule: no dead sections on the homepage.
  if (!loading && sales.length === 0) return null;

  return (
    <section className="py-12 bg-white" data-testid="recent-sales-rail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 bg-rose-50 text-rose-800 border border-rose-100 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Recently sold
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mt-3">
              Deals closed via lovinahome
            </h2>
            <p className="text-gray-600 mt-1 text-sm">
              Live transactions on {market?.name || 'the platform'} — proof our leads convert.
            </p>
          </div>
          <Link
            to={market.withPrefix('/sold')}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[#7042cc] hover:text-[#5b35a8]"
            data-testid="recent-sales-view-all"
          >
            View all sold <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-40 rounded-lg bg-gray-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {sales.map((s) => (
              <RecentSaleCard
                key={s._id || s.id}
                sale={s}
                currency={currency}
                market={market}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const RecentSaleCard = ({ sale, currency, market }) => {
  const id = sale._id || sale.id;
  const cover = sale.images?.[0];
  const agency = sale.external_agent?.agency || sale.external_agent?.name;
  const soldAt = sale.status_updated_at || sale.updatedAt;

  return (
    <Link
      to={market.withPrefix(`/property/${id}`)}
      className="group block bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg hover:border-[#7042cc]/30 transition"
      data-testid={`recent-sale-card-${id}`}
    >
      <div className="relative h-24 bg-gradient-to-br from-rose-100 via-rose-50 to-white">
        {cover && (
          <img
            src={cover}
            alt={sale.title}
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-black/30 pointer-events-none" />
        <div className="absolute top-2 -right-6 w-24 text-center py-0.5 rotate-45 bg-rose-600 text-white text-[9px] font-extrabold tracking-widest uppercase shadow">
          Sold
        </div>
        <div className="absolute bottom-1.5 left-2 text-white text-[10px] font-semibold opacity-90">
          {timeAgo(soldAt)}
        </div>
      </div>
      <div className="p-2.5">
        <div className="text-lg font-extrabold text-[#5b35a8] leading-tight">
          {currency}{Math.round(sale.price || 0).toLocaleString()}
        </div>
        <div className="text-[11px] text-gray-600 leading-tight mt-0.5 flex items-start gap-1">
          <MapPin className="h-3 w-3 mt-0.5 flex-shrink-0 text-gray-400" />
          <span className="line-clamp-2">
            {sale.location?.fullAddress || sale.location?.area}
          </span>
        </div>
        {agency && (
          <div className="text-[10px] text-gray-400 mt-1 truncate">
            via {agency}
          </div>
        )}
      </div>
    </Link>
  );
};

export default RecentSalesRail;
